// src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect } from "react";
import { User } from "../types";
import api from "../api";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      api
        .get("/auth/me")
        .then((response) => {
          if (response.data && response.data.data) {
            setUser(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Auth check failed:", error);
          localStorage.removeItem("adminToken");
          delete api.defaults.headers.common["Authorization"];
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      setUser(null);
    }
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await api.post("/auth/login", { email, password });

      // Check if login was successful
      if (response.data.success) {
        const { token, user } = response.data.data;
        localStorage.setItem("adminToken", token);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setUser(user);
      } else {
        // If success is false, throw the error message
        throw new Error(response.data.message || "Login failed");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      // Re-throw the error so the component can handle it
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
